import type { CourseAttributes, TopicAttributes } from '@/server/models'

import {
  type CertificationRequestType,
  CertificationStatus,
  COURSE_CHALLENGE_TOPIC,
  LIVE_HELP_TOPIC,
  SAT_PILOT_COURSE,
  SAT_PILOT_MATH_TOPIC,
  SAT_PILOT_READING_TOPIC,
  SAT_PREP_COURSE,
  SAT_PREP_MATH_TOPIC,
  SAT_PREP_READING_TOPIC,
  SessionRequestStatus,
  TopicType,
} from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import CertificationRequestQ from './CertificationRequestQ'
import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SessionQ from './SessionQ'
import SessionRequestQ from './SessionRequestQ'
import TopicFollowQ from './TopicFollowQ'

const TopicQ = {
  ...makeQueryModelV2<TopicAttributes>('topic')([
    'id',
    'name',
    'icon',
    'type',
    'description',
    'ka_url',
    'course_id',
    'weight',
    'active',
    'certification_types',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Course: { query: CourseQ, foreignKey: CourseQ.id, sourceKey: TopicQ.course_id },
    CertificationRequests: {
      query: CertificationRequestQ,
      foreignKey: CertificationRequestQ.topic_id,
      sourceKey: TopicQ.id,
    },
  }),

  where: (
    {
      id,
      courseId,
      name,
      type,
      active,
      excludeQa,
      excludeChallenge,
      excludeOther,
      includeInactive = false,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      courseId?: number | null | SQLFragment<number>
      name?: string | null | SQLFragment<string>
      type?: TopicType
      active?: boolean
      excludeQa?: boolean
      excludeChallenge?: boolean
      excludeOther?: boolean
      includeInactive?: boolean
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<TopicAttributes> => SQL`
        FROM ${TopicQ}
        ${includeAssociationsToSql(TopicQ, include)}
        WHERE
            ${id != null ? SQL`${TopicQ.id} = ${id} AND` : SQL``}
            ${courseId != null ? SQL`${TopicQ.course_id} = ${courseId} AND` : SQL``}
            ${name != null ? SQL`${TopicQ.name} = ${name} AND` : SQL``}
            ${type != null ? SQL`${TopicQ.type} = ${type} AND` : SQL``}
            ${active != null ? SQL`${TopicQ.active} = ${active} AND` : SQL``}
            ${excludeQa === true ? SQL`type != 'qa' AND` : SQL``}
            ${excludeChallenge === true ? SQL`type != 'challenge' AND` : SQL``}
            ${excludeOther === true ? SQL`type != 'other' AND` : SQL``}
            ${includeInactive === true ? SQL`` : SQL`active AND`}
            ${paranoid ? SQL`${TopicQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  isActive: (): SQLFragment<boolean> => SQL`${TopicQ.active} = TRUE`,

  getByName: (name: string | null): SQLFragment<CourseAttributes> => SQL`
        ${TopicQ.where({ name })} LIMIT 1
    `,
  getCourse: (): SQLFragment<CourseAttributes> =>
    SQL`FROM ${CourseQ} WHERE ${CourseQ.id} = ${TopicQ.course_id}`,
  getById: (topicId: number): SQLFragment<TopicAttributes> => TopicQ.where({ id: topicId }),
  countSessions: (userId: number): SQLFragment<number> => SQL`
        COUNT(*) ${SessionQ.where({
          topicId: TopicQ.id,
        })} AND ${SessionQ.end_date} > CURRENT_TIMESTAMP
        AND ${SessionQ.userCanViewSession(userId)}
    `,

  hasType: (type: TopicType): SQLFragment<boolean> => SQL`${TopicQ.type} = ${type}`,

  hasAnyType: (types: TopicType[]): SQLFragment<boolean> => SQL`${TopicQ.type} = ANY(${types})`,

  requiresCertification: (): SQLFragment<boolean> =>
    TopicQ.hasAnyType([TopicType.ap, TopicType.challenge, TopicType.unit]),

  countTopicFollows: (): SQLFragment<number> =>
    SQL`COUNT(*) ${TopicFollowQ.where({ topicId: TopicQ.id })}`,
  countOpenSessionRequests: (): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionRequestQ} WHERE ${SessionRequestQ.topic_id} = ${TopicQ.id}
      AND ${SessionRequestQ.status} != ${SessionRequestStatus.Closed}`,
  isUserFollowing: (userId: number | null): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${TopicFollowQ.where({ topicId: TopicQ.id, userId })})`,
  hasUserRequested: (userId: number | null): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionRequestQ.where(
      {},
      SQL` ${SessionRequestQ.topic_id} = ${TopicQ.id} AND ${SessionRequestQ.user_id} = ${userId} `
    )})`,
  getCertifiedTopics: ({
    userId,
    courseId = null,
    reviewed,
    type,
    readyForReview = null,
  }: {
    userId: number | SQLFragment<number> | null
    courseId?: number | SQLFragment<number> | null
    reviewed?: 'approved' | 'rejected' | 'unreviewed' | null
    type?: CertificationRequestType | null
    readyForReview?: boolean | null
  }): SQLFragment<TopicAttributes> => SQL`
            FROM ${TopicQ}
            WHERE EXISTS(
                SELECT 1
                ${CertificationRequestQ.where({
                  topicId: TopicQ.id,
                  userId,
                  reviewed,
                  type,
                  readyForReview,
                })}
            )
            AND ${courseId == null ? SQL`TRUE` : SQL`${TopicQ.course_id} = ${courseId}`}
        `,
  // How it works:
  // - If the topic type is other
  // - If you have an approved certification in this topic
  // - If you have an approved certification in the course challenge topic for a topic's course
  canUserTutor: (
    userId: number | SQLFragment<number>,
    allowInactive = false
  ): SQLFragment<boolean> => SQL`
        (
          ${TopicQ.type} != ${TopicType.challenge} AND
            (${TopicQ.active} IS TRUE OR ${allowInactive} IS TRUE)
        ) AND (
            (
                ${TopicQ.type} = ${TopicType.other}
            ) OR EXISTS(
              SELECT 1
              ${CertificationRequestQ.where(
                {
                  userId,
                  reviewed: 'approved',
                },
                SQL`(
                  (${CertificationRequestQ.topic_id} = ${TopicQ.id}) OR
                  (${CertificationRequestQ.topic_id} = (
                    SELECT t2."id"
                    FROM ${TopicQ} t2
                    WHERE
                    t2."type" = ${TopicType.challenge} AND
                    t2."course_id" = ${TopicQ.course_id}
                  ))
                )`
              )}
            )
        )
    `,
  getTopicCertificationStatus: (userId: number | null): SQLFragment<CertificationStatus> => SQL`
        GREATEST(
            ${CertificationStatus.None}::int,
            (SELECT
                CASE WHEN
                    ${CertificationRequestQ.approved_at} IS NOT NULL
                    THEN ${CertificationStatus.Approved}::int
                    WHEN ${CertificationRequestQ.ready_for_review_at} IS NULL
                    THEN ${CertificationStatus.NotReadyForReview}::int
                    ELSE ${CertificationStatus.Pending}::int
                END
                ${CertificationRequestQ.where(
                  {
                    topicId: TopicQ.id,
                    userId,
                  },
                  SQL`${CertificationRequestQ.rejected_at} IS NULL`
                )}
                ORDER BY ${CertificationRequestQ.approved_at} ASC
                LIMIT 1
            )
        )
    `,

  isNotLiveHelpTopic: (): SQLFragment<boolean> => SQL`(${TopicQ.name} != ${LIVE_HELP_TOPIC})`,

  getSATMathAndReadingTopics: (): SQLFragment<TopicAttributes> =>
    TopicQ.where(
      { include: [{ association: 'Course', required: true }] },
      SQL`
        (${TopicQ.name} = ${SAT_PREP_MATH_TOPIC} AND ${CourseQ.name} = ${SAT_PREP_COURSE}) OR
        (${TopicQ.name} = ${SAT_PREP_READING_TOPIC} AND ${CourseQ.name} = ${SAT_PREP_COURSE}) OR
        (${TopicQ.name} = ${COURSE_CHALLENGE_TOPIC} AND ${CourseQ.name} = ${SAT_PREP_COURSE}) OR
        (${TopicQ.name} = ${SAT_PILOT_MATH_TOPIC} AND ${CourseQ.name} = ${SAT_PILOT_COURSE}) OR
        (${TopicQ.name} = ${SAT_PILOT_READING_TOPIC} AND ${CourseQ.name} = ${SAT_PILOT_COURSE})
      `
    ),
}

export default TopicQ
